import { useState, type ReactNode, type MouseEvent } from 'react';
import Skeleton from 'react-loading-skeleton';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  width?: number;
  /** Clicks inside this column's cell do NOT fire `onRowClick` (e.g. menu col). */
  stopClick?: boolean;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (r: T) => string;
  selectable?: boolean;
  onRowClick?: (r: T) => void;
  empty?: ReactNode;
  loading?: boolean;
}

/** Mockup `.checkbox`: hidden input + 20px box that fills brand + shows a tick when checked. */
function CheckBox({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}): JSX.Element {
  return (
    <label className="inline-flex cursor-pointer select-none items-center">
      <input type="checkbox" className="peer hidden" checked={checked} onChange={onChange} aria-label={label} />
      <span className="inline-flex h-5 w-5 items-center justify-center rounded-md border-[1.5px] border-[var(--m-border)] bg-[var(--m-surface)] text-[var(--m-bg-alt)] transition-colors peer-checked:border-[var(--m-brand)] peer-checked:bg-[var(--m-brand)]">
        {checked && (
          <span
            className="block h-1.5 w-2.5 border-b-2 border-l-2 border-current"
            style={{ transform: 'rotate(-45deg) translate(1px, -1px)' }}
          />
        )}
      </span>
    </label>
  );
}

const HEAD_CLS =
  'whitespace-nowrap border-b border-[var(--m-divider)] bg-[var(--m-surface-muted)] px-5 py-3.5 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--m-fg-muted)]';
const CELL_CLS = 'border-b border-[var(--m-divider)] px-5 py-4 align-middle text-[14px]';

/**
 * Mockup `.data-table`. Optional select-all/per-row checkbox column;
 * clickable rows (checkbox + `stopClick` columns swallow the click);
 * `loading` paints skeleton rows; `empty` renders when there are no rows.
 */
export function DataTable<T>({
  columns,
  rows,
  getRowId,
  selectable = false,
  onRowClick,
  empty,
  loading = false,
}: DataTableProps<T>): JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(getRowId(r)));
  const colCount = columns.length + (selectable ? 1 : 0);

  const toggleRow = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = (): void =>
    setSelected(allSelected ? new Set() : new Set(rows.map(getRowId)));

  const stop = (e: MouseEvent): void => e.stopPropagation();

  if (!loading && rows.length === 0 && empty) {
    return <>{empty}</>;
  }

  return (
    <table className="w-full border-collapse">
      <thead>
        <tr>
          {selectable && (
            <th className={HEAD_CLS} style={{ width: 40 }}>
              <CheckBox checked={allSelected} onChange={toggleAll} label="Selecionar todos" />
            </th>
          )}
          {columns.map((col) => (
            <th key={col.key} className={HEAD_CLS} style={col.width ? { width: col.width } : undefined}>
              {col.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading
          ? Array.from({ length: 5 }).map((_, i) => (
              <tr key={`sk-${i}`}>
                {Array.from({ length: colCount }).map((__, j) => (
                  <td key={j} className={CELL_CLS}>
                    <Skeleton />
                  </td>
                ))}
              </tr>
            ))
          : rows.map((row) => {
              const id = getRowId(row);
              return (
                <tr
                  key={id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? 'cursor-pointer hover:bg-[var(--m-surface-muted)]' : undefined}
                >
                  {selectable && (
                    <td className={CELL_CLS} onClick={stop}>
                      <CheckBox checked={selected.has(id)} onChange={() => toggleRow(id)} label={`Selecionar ${id}`} />
                    </td>
                  )}
                  {columns.map((col) => (
                    <td key={col.key} className={CELL_CLS} onClick={col.stopClick ? stop : undefined}>
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
      </tbody>
    </table>
  );
}
